import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  MapPin,
  Download,
  Eye,
  Mail,
  Phone,
  Home,
  FileText,
  Layers,
} from 'lucide-react';

// Import styled components
import {
  PageContainer,
  ContentContainer,
  MainContent,
  Sidebar,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  DetailItem,
  DetailLabel,
  DetailValue,
  DetailGrid,
  ContactItem,
  ContactIcon,
  ContactText,
  ContactInfo,
  Button,
  Badge,
  MapContainer,
  MapIcon,
  MapText,
  MapSubText,
  ReportItemContainer,
  ReportInfo,
  ReportTitle,
  ReportStatus,
  ReportActions,
  ReportSection,
  OverlayContainer,
  ActionButtonGroup,
  ApplicationHeader,
  ApplicationTitle,
  ApplicationId,
  OverlayDescription,
  DevelopmentDescription,
} from './analyticsDetails.style';

// Updated DetailItem Component
const DetailItemComponent = ({ label, value }) => (
  <DetailItem>
    <DetailLabel>{label}</DetailLabel>
    <DetailValue>{value}</DetailValue>
  </DetailItem>
);

// Updated ContactItem Component
const ContactItemComponent = ({ icon: Icon, text, href }) => (
  <ContactItem href={href}>
    <ContactIcon>
      <Icon size={20} />
    </ContactIcon>
    <ContactText>{text}</ContactText>
  </ContactItem>
);

// Updated PropertyMap Component
const PropertyMap = () => (
  <MapContainer>
    <MapIcon>
      <MapPin size={24} />
    </MapIcon>
    <MapText>
      Property Location Map
      <br />
      <MapSubText>41 Coolgarra Avenue, Bongaree</MapSubText>
    </MapText>
  </MapContainer>
);

// Updated ReportItem Component
const ReportItem = ({ title, status, type, onView, onDownload }) => (
  <ReportItemContainer>
    <ReportInfo>
      <ReportTitle>{title}</ReportTitle>
      <ReportStatus>Status: {status}</ReportStatus>
    </ReportInfo>
    <ReportActions>
      <Button variant="secondary" onClick={onView}>
        <Eye size={16} />
        View
      </Button>
      <Button variant="primary" onClick={onDownload}>
        <Download size={16} />
        Download
      </Button>
    </ReportActions>
  </ReportItemContainer>
);

// Collapsible Card Component
const CollapsibleCard = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Card>
      <CardHeader clickable onClick={() => setIsOpen(!isOpen)}>
        <CardTitle>
          {Icon && <Icon size={20} />}
          {title}
        </CardTitle>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </CardHeader>
      {isOpen && <CardContent>{children}</CardContent>}
    </Card>
  );
};

// Main Application Details Component
const AnalyticsDetailsPage = () => {
  const applicationData = {
    id: 'DA-2024-001234',
    status: 'Under Review',
    property: {
      address: '41 COOLGARRA AVENUE BONGAREE',
      division: 'DIVISION 1',
      district: 'CABOOLTURE',
      description:
        'Single dwelling house with attached garage and pool area. Development includes landscaping and driveway modifications.',
    },
    applicant: {
      name: 'Madhurjya Gogoi',
      email: 'test@gmail.com',
      phone: '+612222222222',
    },
    overlays: [
      'Flood Overlay',
      'Bushfire Overlay',
      'Heritage Overlay',
      'Environmental Overlay',
      'Transport Overlay',
    ],
    reports: [
      { title: 'Pre-lodgement Report', status: 'Completed', type: 'prelodge' },
      { title: 'Final Assessment Report', status: 'In Progress', type: 'final' },
    ],
  };

  const handleReportView = (reportType) => {
    console.log(`Viewing ${reportType} report`);
  };

  const handleReportDownload = (reportType) => {
    console.log(`Downloading ${reportType} report`);
  };

  return (
    <PageContainer>
      <ContentContainer>
        <MainContent>
          {/* Application Header */}
          <Card>
            <CardContent>
              <ApplicationHeader>
                <div>
                  <ApplicationTitle>Development Application</ApplicationTitle>
                  <ApplicationId>Application ID: {applicationData.id}</ApplicationId>
                  <Badge variant="status">{applicationData.status}</Badge>
                </div>
              </ApplicationHeader>
            </CardContent>
          </Card>

          {/* Property Details */}
          <CollapsibleCard title="Property Details" icon={Home}>
            <DetailGrid>
              <DetailItemComponent label="Address" value={applicationData.property.address} />
              <DetailItemComponent label="Division" value={applicationData.property.division} />
              <DetailItemComponent label="District" value={applicationData.property.district} />
            </DetailGrid>
            <DevelopmentDescription>
              <DetailItemComponent
                label="Description"
                value={applicationData.property.description}
              />
            </DevelopmentDescription>
          </CollapsibleCard>

          {/* Development Details */}
          <CollapsibleCard title="Development Information" icon={FileText}>
            <DetailGrid>
              <DetailItemComponent label="Application Type" value="Single Dwelling Development" />
              <DetailItemComponent label="Zoning" value="Low Density Residential" />
              <DetailItemComponent label="Land Area" value="650 m²" />
              <DetailItemComponent label="Building Height" value="8.5 meters" />
              <DetailItemComponent label="Site Coverage" value="35%" />
              <DetailItemComponent label="Setbacks" value="Front: 6m, Side: 1.5m, Rear: 6m" />
            </DetailGrid>
          </CollapsibleCard>

          {/* Applicable Overlays */}
          <CollapsibleCard title="Applicable Overlays" icon={Layers}>
            <OverlayContainer>
              {applicationData.overlays.map((overlay, index) => (
                <Badge key={index} variant="overlay">
                  {overlay}
                </Badge>
              ))}
            </OverlayContainer>
            <OverlayDescription>
              These overlays may affect the development requirements and assessment process.
            </OverlayDescription>
          </CollapsibleCard>

          {/* Reports Section */}
          <CollapsibleCard title="Reports & Documents" icon={FileText}>
            <ReportSection>
              {applicationData.reports.map((report, index) => (
                <ReportItem
                  key={index}
                  title={report.title}
                  status={report.status}
                  type={report.type}
                  onView={() => handleReportView(report.type)}
                  onDownload={() => handleReportDownload(report.type)}
                />
              ))}
            </ReportSection>
          </CollapsibleCard>
        </MainContent>

        <Sidebar>
          {/* Property Map */}
          <Card>
            <CardHeader>
              <CardTitle>
                <MapPin size={20} />
                Property Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PropertyMap />
            </CardContent>
          </Card>

          {/* Applicant Information */}
          <Card>
            <CardHeader>
              <CardTitle>
                <FileText size={20} />
                Applicant Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ContactInfo>
                <DetailItemComponent label="Name" value={applicationData.applicant.name} />
                <ContactItemComponent
                  icon={Mail}
                  text={applicationData.applicant.email}
                  href={`mailto:${applicationData.applicant.email}`}
                />
                <ContactItemComponent
                  icon={Phone}
                  text={applicationData.applicant.phone}
                  href={`tel:${applicationData.applicant.phone}`}
                />
              </ContactInfo>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <ActionButtonGroup>
                <Button variant="primary">
                  <FileText size={16} />
                  Submit Amendment
                </Button>
                <Button variant="secondary">
                  <Download size={16} />
                  Download All Documents
                </Button>
                <Button variant="secondary">
                  <Mail size={16} />
                  Contact Officer
                </Button>
              </ActionButtonGroup>
            </CardContent>
          </Card>
        </Sidebar>
      </ContentContainer>
    </PageContainer>
  );
};

export default AnalyticsDetailsPage;
